/**
 * Thème Chakra de la v2 (docs/ux-v2/SPEC.md § 6.4).
 *
 * Construit SUR le thème v1 (`theme/index.ts`, importé tel quel, non modifié) : tant que la v2
 * affiche encore des écrans v1 (enveloppe AppV2, écrans repris un à un), ils gardent leurs
 * variantes (`contained`, palette `primary`…). La v2 n'ajoute que des entrées préfixées `mmv2` :
 * couleurs `mmv2.*`, polices `mmv2` / `mmv2Display`, rayons `mmv2Card`…, variantes de Button
 * `mmv2Cta`, `mmv2Primary`, `mmv2Dark`, `mmv2Ghost`, `mmv2Quiet`, tailles `mmv2Sm|Md|Lg`, variante
 * de Modal `mmv2`. Les fonts body / heading et les styles globaux restent ceux de la v1 : la page
 * WordPress autour de l'app n'est pas touchée.
 *
 * Valeurs littérales issues de resolveTokens() (et pas de var(--mmv2-*)) : les modales et toasts
 * Chakra sont rendus dans un portail hors de #mindbody-app, où ces variables n'existent pas.
 *
 * Fonction appelée seulement quand la v2 est active : aucun calcul au chargement du module.
 */
import { extendTheme, type ThemeOverride } from '@chakra-ui/react';
import baseTheme from '@theme/index';
import { mix } from '../lib/color';
import type { V2Tokens } from './tokens';

/** Boutons de la maquette : pilule, capitales 600, tracking +.11em (`.btn`). */
const buttonBase = (tokens: V2Tokens) => ({
  fontFamily: 'mmv2',
  fontWeight: 600,
  letterSpacing: '0.11em',
  textTransform: 'uppercase' as const,
  borderRadius: 'mmv2Pill',
  transition: `transform .16s ${tokens.ease}, background .16s ${tokens.ease}, color .16s ${tokens.ease}, box-shadow .16s ${tokens.ease}`,
  _active: { transform: 'translateY(1px)' },
  _disabled: { opacity: 0.42, cursor: 'not-allowed', boxShadow: 'none' },
  _focusVisible: { outline: `2.5px solid ${tokens.focus}`, outlineOffset: '2px', boxShadow: 'none' },
});

export function createThemeV2(tokens: V2Tokens) {
  const overrides: ThemeOverride = {
    colors: {
      mmv2: {
        brand: tokens.brand,
        brandSurface: tokens.brandSurface,
        brandHover: tokens.brandHover,
        brandText: tokens.brandText,
        brandOn: tokens.brandOn,
        brand50: tokens.brand50,
        brand100: tokens.brand100,
        action: tokens.action,
        actionSurface: tokens.actionSurface,
        actionHover: tokens.actionHover,
        actionText: tokens.actionText,
        actionOn: tokens.actionOn,
        action50: tokens.action50,
        actionBorder: tokens.actionBorder,
        ink: tokens.ink,
        ink2: tokens.ink2,
        ink3: tokens.ink3,
        ink4: tokens.ink4,
        paper: tokens.paper,
        canvas: tokens.canvas,
        sunken: tokens.sunken,
        line: tokens.line,
        line2: tokens.line2,
        fieldBorder: tokens.fieldBorder,
        green: tokens.green,
        greenText: tokens.greenText,
        green50: tokens.green50,
        amber: tokens.amber,
        amberText: tokens.amberText,
        amber50: tokens.amber50,
        red: tokens.red,
        redText: tokens.redText,
        redHover: tokens.redHover,
        red50: tokens.red50,
        focus: tokens.focus,
      },
      /*
       * Gris de Chakra utilisés EN LIGNE par les composants v1 rendus sous la v2 (calques de paiement Alma, portefeuille,
       * 3-D Secure d'un abonnement ; formulaires de connexion) : encres du site, lisibles (revue du 05/10 : textes
       * gris-bleu à 4,02:1, « En cas de problème… » à 2,26:1). Teintes de fond (50 à 300) inchangées.
       */
      gray: {
        400: tokens.ink3,
        500: tokens.ink3,
        600: tokens.ink2,
        700: tokens.ink2,
        800: tokens.ink,
        900: tokens.ink,
      },
      /*
       * Palettes de Chakra employées EN LIGNE par les calques de paiement v1 (Alma : anneau du logo blue.50, spinner
       * blue.500, bouton « Contacter le support » colorScheme blue, encadrés red.50 / orange.50 ; échec 3-D Secure d'un
       * abonnement : encadré red.50 / red.200) : couleurs du site, plus le bleu ni le rouge et l'orange de Chakra
       * (revue du 05/10 : bouton bleu #3182CE, blanc sur bleu à 4,03:1, sous l'AA ; anneau bleu autour du logo Alma).
       */
      blue: {
        50: tokens.brand50,
        100: tokens.brand100,
        200: tokens.brand100,
        300: tokens.brand,
        400: tokens.brand,
        500: tokens.brandSurface,
        600: tokens.brandHover,
        700: tokens.brandText,
        800: tokens.brandText,
        900: tokens.ink,
      },
      red: {
        50: tokens.red50,
        100: tokens.red50,
        200: mix(tokens.red, '#ffffff', 0.3),
        300: mix(tokens.red, '#ffffff', 0.55),
        400: tokens.red,
        500: tokens.red,
        600: tokens.redText,
        700: tokens.redText,
        800: tokens.redText,
        900: tokens.ink,
      },
      orange: {
        50: tokens.amber50,
        100: tokens.amber50,
        200: mix(tokens.amber, '#ffffff', 0.4),
        300: tokens.amber,
        400: tokens.amber,
        500: tokens.amber,
        600: tokens.amberText,
        700: tokens.amberText,
        800: tokens.amberText,
        900: tokens.ink,
      },
    },
    fonts: {
      mmv2: tokens.font,
      mmv2Display: tokens.fontDisplay,
    },
    radii: {
      mmv2Card: tokens.rCard,
      mmv2Pill: tokens.rPill,
      // Calques de paiement v1 (ModalContent borderRadius="2xl") : arrondi des cartes du site (0 sur Dynamo).
      '2xl': tokens.rCard,
      // Encadrés et boutons v1 des calques (borderRadius « md » / « lg », 6 et 8 px de Chakra) : arrondis du site.
      md: tokens.rInput,
      lg: tokens.rCard,
      mmv2Chip: tokens.rChip,
      mmv2Input: tokens.rInput,
      mmv2Tag: tokens.rTag,
    },
    shadows: {
      mmv2Sm: tokens.shadow1,
      mmv2Lg: tokens.shadow2,
      /*
       * Anneau de focus des composants Chakra (Checkbox, Link, Switch… : `boxShadow: 'outline'`) : couleur de focus du
       * site, plus le bleu de Chakra (revue du 05/10 : cases de l'inscription et du panier, « Mot de passe oublié ? »).
       */
      outline: `0 0 0 2.5px ${tokens.focus}`,
    },
    components: {
      /*
       * Focus clavier des composants Chakra v1 rendus sous la v2 : le contour du site (2,5 px décalé de 2 px), comme
       * tous les contrôles de la v2, et non un anneau collé (case à cocher) ni un double anneau (lien : contour du
       * site + ombre). Case : la case visible porte le contour, l'input masqué n'en a pas.
       */
      Checkbox: {
        baseStyle: {
          control: {
            _focusVisible: { boxShadow: 'none', outline: `2.5px solid ${tokens.focus}`, outlineOffset: '2px' },
          },
        },
      },
      Link: {
        baseStyle: {
          _focusVisible: { boxShadow: 'none', outline: `2.5px solid ${tokens.focus}`, outlineOffset: '2px' },
        },
      },
      // Titres Chakra des calques de paiement v1 : police d'affichage du site (police système en gras sinon).
      Heading: {
        baseStyle: { fontFamily: 'mmv2Display', fontWeight: 600, letterSpacing: 0, wordSpacing: tokens.displayWordSpacing },
      },
      // Barres de progression des calques de paiement (colorScheme « orange » en ligne) : couleur de marque.
      Progress: {
        baseStyle: { filledTrack: { bgColor: tokens.brandSurface } },
      },
      Button: {
        variants: {
          /*
           * Bouton plein de couleur « blue » (calque d'échec Alma : « Contacter le support ») : le bouton principal de
           * la v2 (surface et texte de marque, AA, pilule, capitales), pas un bleu de Chakra. Couleur « gray » (même
           * calque : « Fermer et voir mes crédits », « Fermer ») : le bouton à contour de la v2 (mmv2Ghost), plus le
           * gris de Chakra à coins carrés (revue r4). Seuls boutons pleins de ces couleurs rendus sous la v2
           * (composants v1 compris). Autres couleurs : styles de la v1 (variante fusionnée avec celle du thème v1 ;
           * objet vide).
           */
          solid: (props: { colorScheme?: string }) => {
            if (props.colorScheme === 'blue') {
              return {
                ...buttonBase(tokens),
                bg: 'mmv2.brandSurface',
                color: 'mmv2.brandOn',
                _hover: { bg: 'mmv2.brandHover', _disabled: { bg: 'mmv2.brandSurface' } },
                _active: { bg: 'mmv2.brandHover' },
              };
            }
            if (props.colorScheme === 'gray') {
              const base = buttonBase(tokens);
              // Fonds de survol et d'appui du gris de Chakra (gray.200, gray.300) remplacés : variante fusionnée.
              return {
                ...base,
                bg: 'mmv2.paper',
                color: 'mmv2.ink',
                border: '1.5px solid',
                borderColor: 'mmv2.line',
                // Capitales espacées : corps du « Contacter le support » voisin (size sm, 14 px), pas 16 px (md).
                fontSize: 'sm',
                _hover: {
                  bg: 'mmv2.paper',
                  borderColor: 'mmv2.ink',
                  _disabled: { bg: 'mmv2.paper', borderColor: 'mmv2.line' },
                },
                _active: { ...base._active, bg: 'mmv2.paper' },
              };
            }
            return {};
          },
          // .btn-cta : la seule couleur d'action de la page (surface AA pour son texte, tokens.ts).
          mmv2Cta: {
            ...buttonBase(tokens),
            bg: 'mmv2.actionSurface',
            color: 'mmv2.actionOn',
            _hover: { bg: 'mmv2.actionHover', _disabled: { bg: 'mmv2.actionSurface' } },
          },
          // .btn-primary : surface de marque.
          mmv2Primary: {
            ...buttonBase(tokens),
            bg: 'mmv2.brandSurface',
            color: 'mmv2.brandOn',
            _hover: { bg: 'mmv2.brandHover', _disabled: { bg: 'mmv2.brandSurface' } },
          },
          // .btn-dark
          mmv2Dark: {
            ...buttonBase(tokens),
            bg: 'mmv2.ink',
            color: 'mmv2.paper',
            _hover: { bg: 'mmv2.ink2', _disabled: { bg: 'mmv2.ink' } },
          },
          // .btn-ghost : contour.
          mmv2Ghost: {
            ...buttonBase(tokens),
            bg: 'mmv2.paper',
            color: 'mmv2.ink',
            border: '1.5px solid',
            borderColor: 'mmv2.line',
            _hover: { borderColor: 'mmv2.ink', _disabled: { borderColor: 'mmv2.line' } },
          },
          // .btn-quiet : action discrète (ex. « Se déconnecter », SPEC MC2 en décidera).
          mmv2Quiet: {
            ...buttonBase(tokens),
            bg: 'transparent',
            color: 'mmv2.ink2',
            // Désactivé (pendant un envoi) : aucun effet de survol.
            _hover: { bg: 'mmv2.sunken', color: 'mmv2.ink', _disabled: { bg: 'transparent', color: 'mmv2.ink2' } },
          },
        },
        // Interligne « normal » des contrôles d'une ligne (styles/ux-v2.css) : texte centré au pixel (1,2 px trop haut
        // avec l'interligne du thème, revue du 04/10).
        sizes: {
          mmv2Sm: { h: '38px', minW: '38px', px: '16px', fontSize: '11.5px', lineHeight: 'normal' },
          mmv2Md: { h: '46px', minW: '46px', px: '22px', fontSize: '13px', lineHeight: 'normal' },
          mmv2Lg: { h: '54px', minW: '54px', px: '30px', fontSize: '14px', lineHeight: 'normal' },
        },
      },
      Modal: {
        variants: {
          // .overlay / .modal de la maquette.
          mmv2: {
            overlay: { bg: 'rgba(11, 12, 17, 0.5)', backdropFilter: 'blur(3px)' },
            dialog: {
              bg: 'mmv2.paper',
              color: 'mmv2.ink',
              fontFamily: 'mmv2',
              // Arrondi des cartes du site (jeton rCard : 0 sur Dynamo, coins droits), pas celui du prototype.
              borderRadius: 'mmv2Card',
              boxShadow: 'mmv2Lg',
              mx: '20px',
            },
            // Titre à 22 px : pas d'interlettrage négatif sous 24 px (« 4SÉANCES PARMOIS », revue du 04/10) ; interligne
            // entier de 26 px, sur lequel la croix est centrée.
            header: {
              fontFamily: 'mmv2Display',
              fontSize: '22px',
              fontWeight: 600,
              lineHeight: '26px',
              textTransform: 'uppercase',
              letterSpacing: 0,
              wordSpacing: tokens.displayWordSpacing,
              pt: { base: '24px', sm: '30px' },
              px: { base: '20px', sm: '32px' },
              pr: { base: '64px', sm: '76px' },
            },
            // Croix ronde de 38 px de toutes les modales v2 (Mon compte, fiche cours, Tarifs), centrée sur la première
            // ligne du titre : haut de l'en-tête + 13 px (demi-interligne) − 19 px (demi-croix). Contour de focus du site,
            // jamais l'anneau bleu de Chakra.
            closeButton: {
              top: { base: '18px', sm: '24px' },
              insetEnd: { base: '12px', sm: '20px' },
              w: '38px',
              h: '38px',
              borderRadius: '999px',
              color: 'mmv2.ink2',
              _hover: { bg: 'mmv2.sunken', color: 'mmv2.ink' },
              _focusVisible: { outline: `2.5px solid ${tokens.focus}`, outlineOffset: '2px', boxShadow: 'none' },
            },
            body: { px: { base: '20px', sm: '32px' } },
            footer: { px: { base: '20px', sm: '32px' }, pb: { base: '24px', sm: '30px' } },
          },
        },
      },
    },
  };

  return extendTheme(overrides, baseTheme);
}
